import * as React from 'react';
import { t } from '@reos/i18n';
import { Check, Mic, Square, X } from 'lucide-react';
import type { DictationSuggestionDto } from '../../lib/field-intake-api.js';
import { DICTATION_MAX_SECONDS, type DictationState } from '../../lib/intake-dictation.js';
import type { Recorder, RecorderHandle } from '../../lib/field-intake-recorder.js';

/**
 * Krok NAB4 — MIKROFON U SKUPINY A U MISTNOSTI a navrh u bodu.
 *
 * Diktat je KRATKY (strop 60 s, nahravani se samo zastavi) a jde do stejne
 * offline fronty jako celoschuzkova nahravka. Navrh pak „sviti" u bodu
 * s citaci vety a makler ho potvrdi JEDNIM klepnutim — bez potvrzeni se
 * nezapise nic.
 */

const tr = (key: string, values?: Record<string, string | number>): string =>
  t(`webAdmin.fieldIntake.dictation.${key}` as Parameters<typeof t>[0], {
    locale: 'cs',
    ...(values !== undefined ? { values } : {}),
  });

export function DictationMic({
  recorder,
  state,
  testid,
  onRecorded,
}: {
  readonly recorder: Recorder;
  readonly state: DictationState | undefined;
  readonly testid: string;
  readonly onRecorded: (blob: Blob, durationS: number) => void;
}): React.JSX.Element {
  const [recording, setRecording] = React.useState(false);
  const [elapsed, setElapsed] = React.useState(0);
  const [denied, setDenied] = React.useState(false);
  const handle = React.useRef<RecorderHandle | null>(null);

  const stop = React.useCallback(async (): Promise<void> => {
    const current = handle.current;
    if (current === null) return;
    handle.current = null;
    setRecording(false);
    setElapsed(0);
    const result = await current.stop();
    onRecorded(result.blob, result.durationS);
  }, [onRecorded]);

  /** Strop diktatu: po 60 s se nahravani zastavi samo a posle se k rozboru. */
  React.useEffect(() => {
    if (!recording) return undefined;
    const timer = window.setInterval(() => {
      setElapsed((value) => {
        const next = value + 1;
        if (next >= DICTATION_MAX_SECONDS) void stop();
        return next;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [recording, stop]);

  const start = async (): Promise<void> => {
    setDenied(false);
    try {
      handle.current = await recorder.start();
      setElapsed(0);
      setRecording(true);
    } catch {
      setDenied(true);
    }
  };

  const pending = state?.status === 'pending';

  return (
    <div className="rh-fi-dict" data-testid={`${testid}-mic-box`}>
      <button
        type="button"
        className={`rh-v4-btn rh-v4-btn--chip rh-fi-dict__mic${recording ? ' is-on' : ''}`}
        aria-pressed={recording}
        disabled={!recorder.supported || pending}
        onClick={() => void (recording ? stop() : start())}
        data-testid={`${testid}-mic`}
      >
        {recording ? <Square size={18} aria-hidden="true" /> : <Mic size={18} aria-hidden="true" />}
        {recording ? tr('stop', { seconds: elapsed }) : tr('record')}
      </button>
      {pending ? (
        <span className="rh-fi-cl__where" data-testid={`${testid}-pending`}>
          {tr('pending')}
        </span>
      ) : null}
      {state?.status === 'unavailable' ? (
        <p className="rh-fi-note rh-fi-note--warn" role="alert" data-testid={`${testid}-unavailable`}>
          {tr('unavailable')}
        </p>
      ) : null}
      {denied ? (
        <p className="rh-fi-note rh-fi-note--warn" role="alert" data-testid={`${testid}-denied`}>
          {t('webAdmin.fieldIntake.recording.micDenied', { locale: 'cs' })}
        </p>
      ) : null}
      {state?.status === 'ready' && state.text !== '' ? (
        <p className="rh-fi-cl__where" data-testid={`${testid}-text`}>
          {tr('heard', { text: state.text })}
        </p>
      ) : null}
      {state?.status === 'ready' && state.suggestions.length === 0 ? (
        <p className="rh-fi-note" data-testid={`${testid}-none`}>
          {tr('none')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Navrh u bodu: hodnota, citace vety a jedno klepnuti „Potvrdit". Hodnota mimo
 * moznosti je „Jiné…" s textem — makler vidi, ze se zapise text, ne ciselnik.
 */
export function DictationSuggestion({
  suggestion,
  testid,
  onAccept,
  onDismiss,
}: {
  readonly suggestion: DictationSuggestionDto;
  readonly testid: string;
  readonly onAccept: () => void;
  readonly onDismiss: () => void;
}): React.JSX.Element {
  return (
    <div className="rh-fi-dict__suggest" data-testid={`${testid}-suggest`}>
      <span className="rh-fi-dict__value">
        {suggestion.other ? `${tr('other')} ${suggestion.label}` : suggestion.label}
      </span>
      <span className="rh-fi-dict__quote" data-testid={`${testid}-quote`}>
        {tr('quote', { quote: suggestion.quote })}
      </span>
      <button
        type="button"
        className="rh-v4-btn rh-v4-btn--chip rh-fi-dict__accept"
        onClick={onAccept}
        data-testid={`${testid}-accept`}
      >
        <Check size={18} aria-hidden="true" />
        {tr('accept')}
      </button>
      <button
        type="button"
        className="rh-v4-btn rh-v4-btn--plain"
        onClick={onDismiss}
        data-testid={`${testid}-dismiss`}
      >
        <X size={18} aria-hidden="true" />
        {tr('dismiss')}
      </button>
    </div>
  );
}
